"use client";

import type { MinimalGlossaryTerm } from "@/app/(marketing)/(payload)/dictionary/_actions/glossary";
import { GlossaryText } from "@/components/common/glossary-text";
import { useId } from "react";
import Link from "next/link";
import { ArrowRight, RotateCcw } from "@repo/icons/ui";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { inputClassName } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  calculateFunding,
  initialFundingDraft,
  type NumericField,
  type FundingErrors,
} from "@/lib/calculators/acquisition-funding";
import { FundingField } from "./funding-field";
import { FundingResults } from "./funding-results";
import { FundingSummary } from "./funding-summary";
import { useFundingScenario, fundingCurrencies } from "./use-funding-scenario";
import "./calculator.css";

export function AcquisitionCalculator({
  compact = false,
  terms = [],
}: {
  compact?: boolean;
  terms?: MinimalGlossaryTerm[];
}) {
  const prefix = useId();
  const {
    draft,
    setDraft,
    currency,
    setCurrency,
    sellerEnabled,
    setSellerEnabled,
  } = useFundingScenario();
  const outcome = calculateFunding(draft);
  const errors: FundingErrors = outcome.ok ? {} : outcome.errors;
  function change(name: NumericField, value: string) {
    setDraft((old) => ({ ...old, [name]: value }));
  }
  function field(
    name: NumericField,
    label: string,
    unit = currency,
    hint?: string,
  ) {
    return (
      <FundingField
        key={name}
        {...{ name, label, unit, hint, terms, prefix, draft, onChange: change }}
        error={errors[name]}
      />
    );
  }
  return (
    <div
      className="acquisition-calculator ph-no-capture ph-mask space-y-6"
      data-ph-no-capture
    >
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="w-40 space-y-2">
          <Label htmlFor={`${prefix}-currency`}>Currency</Label>
          <select
            id={`${prefix}-currency`}
            className={`${inputClassName} h-12`}
            value={currency}
            onChange={(event) => setCurrency(event.target.value)}
          >
            {fundingCurrencies.map((code) => (
              <option key={code}>{code}</option>
            ))}
          </select>
        </div>
        <Button
          type="button"
          variant="ghost"
          className="min-h-11"
          onClick={() => {
            setDraft({ ...initialFundingDraft });
            setCurrency("USD");
            setSellerEnabled(false);
          }}
        >
          <RotateCcw className="size-4" aria-hidden />
          Reset example
        </Button>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">
        These are example numbers to help you get started. Replace them with
        your own figures. Changing the currency changes the label, not the
        amounts.
      </p>
      <div
        className={
          compact
            ? "space-y-6"
            : "grid items-start gap-10 lg:grid-cols-[1.15fr_1fr] lg:gap-16"
        }
      >
        <div className="space-y-8">
          <FundingSummary
            result={outcome.ok ? outcome.value : null}
            currency={currency}
            compact={compact}
          />
          <fieldset className="space-y-5">
            <legend className="mb-4 font-display text-xl font-medium">
              The purchase and your financing
            </legend>
            <div className="grid gap-5 sm:grid-cols-2">
              {field("price", "Purchase price")}
              {field(
                "bankAmount",
                "Lender loan amount",
                currency,
                "Money you expect to borrow from a bank or other lender. Ask which purchase costs the loan will cover.",
              )}
            </div>
          </fieldset>
          <details open={!compact} className="border-y border-border py-5">
            <summary className="min-h-11 cursor-pointer font-medium">
              Loan terms and seller financing
            </summary>
            <div className="space-y-6 pt-5">
              <p className="text-sm leading-relaxed text-muted-foreground">
                <GlossaryText
                  terms={terms}
                  text="Each monthly payment pays back some of the amount borrowed (the principal), plus interest. This estimate uses a fixed interest rate for the whole loan. Change the example terms to match the loan you are discussing."
                />
              </p>
              <div className="grid gap-5 sm:grid-cols-2">
                {field(
                  "bankRate",
                  "Lender annual interest rate",
                  "%",
                  "The yearly loan interest rate, not an APR that includes fees.",
                )}
                {field(
                  "bankMonths",
                  "Lender repayment term",
                  "months",
                  "The number of monthly payments. For example, 120 months is 10 years.",
                )}
              </div>
              <label
                htmlFor={`${prefix}-seller`}
                className="flex min-h-11 cursor-pointer items-center gap-3 text-sm font-medium"
              >
                <Checkbox
                  id={`${prefix}-seller`}
                  checked={sellerEnabled}
                  onCheckedChange={(checked) => {
                    setSellerEnabled(checked === true);
                    setDraft((old) => ({
                      ...old,
                      sellerAmount: checked === true ? "30000" : "0",
                      sellerInterestOnly: false,
                    }));
                  }}
                />
                Include seller financing
              </label>
              <p className="text-xs leading-relaxed text-muted-foreground">
                <GlossaryText
                  terms={terms}
                  text="Seller financing means paying part of the price to the seller over time. It needs the seller’s agreement and your lender may have conditions."
                />
              </p>
              {sellerEnabled && (
                <div className="space-y-5 border-l-2 border-border pl-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    {field("sellerAmount", "Seller-financed amount")}
                    {field("sellerRate", "Seller annual interest rate", "%")}
                    {field("sellerMonths", "Seller repayment term", "months")}
                  </div>
                  <label
                    htmlFor={`${prefix}-interest-only`}
                    className="flex min-h-11 cursor-pointer items-center gap-3 text-sm"
                  >
                    <Checkbox
                      id={`${prefix}-interest-only`}
                      checked={draft.sellerInterestOnly}
                      onCheckedChange={(checked) =>
                        setDraft((old) => ({
                          ...old,
                          sellerInterestOnly: checked === true,
                        }))
                      }
                    />
                    Pay seller interest only; repay the amount borrowed at the
                    end
                  </label>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    <GlossaryText
                      terms={terms}
                      text="Interest-only payments leave the amount borrowed to repay at the end, called a balloon payment. Otherwise, the monthly payments repay the loan fully. Paused payments, earn-outs and changing rates need a separate calculation."
                    />
                  </p>
                </div>
              )}
            </div>
          </details>
          <fieldset className="space-y-5">
            <legend className="mb-4 font-display text-xl font-medium">
              Cash beyond the purchase price
            </legend>
            <div className="grid gap-5 sm:grid-cols-2">
              {field(
                "fees",
                "Adviser and closing costs",
                currency,
                "Closing costs can include legal, accounting, lender and transfer fees. Add any taxes you expect to pay when you buy.",
              )}
              {field(
                "workingCapital",
                "Additional working capital",
                currency,
                "Extra cash for stock, payroll and bills after the purchase. Add only the working capital you need beyond what is already included in the price.",
              )}
              {field(
                "reserve",
                "Business reserve",
                currency,
                "A separate cushion for unexpected costs. Keep your personal living budget separate.",
              )}
              {field(
                "partnerEquity",
                "Partner / investor equity",
                currency,
                "Money an owner or partner puts in is called an equity injection. Enter cash here, not another loan. This estimate does not work out ownership shares or investor returns.",
              )}
            </div>
          </fieldset>
          {!compact && (
            <fieldset className="space-y-5 border-t border-border pt-6">
              <legend className="float-left mb-4 w-full font-display text-xl font-medium">
                Compare with your available cash
              </legend>
              <div className="clear-both">
                {field(
                  "availableCash",
                  "Your available cash (optional)",
                  currency,
                  "Money you have available for this purchase. Leave blank to skip, and keep your personal living budget separate.",
                )}
              </div>
            </fieldset>
          )}
        </div>
        <div className={compact ? "" : "lg:sticky lg:top-24"}>
          <FundingResults
            result={outcome.ok ? outcome.value : null}
            currency={currency}
            compact={compact}
            terms={terms}
          />
        </div>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        A planning estimate for conversations with your advisers, not a loan
        offer or an approval check. It assumes funds are available when you take
        ownership and rates stay fixed. Include applicable taxes and fees in
        your costs. Investor returns are not calculated.
      </p>
      {compact && (
        <Button
          variant="outline"
          asChild
          className="h-auto min-h-12 whitespace-normal text-left"
        >
          <Link href="/business-acquisition-calculator" prefetch={false}>
            Open the full acquisition calculator
            <ArrowRight className="size-4 shrink-0" aria-hidden />
          </Link>
        </Button>
      )}
    </div>
  );
}
